'use client';
import React, { useEffect, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import { readStoredLanguage } from 'features/language/languageConfig';
import '@fortawesome/fontawesome-free/css/all.min.css';
import dayGridPlugin from '@fullcalendar/daygrid';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin from '@fullcalendar/interaction';
import Link from 'next/link';
import UserShortCut from 'components/common/UserShortCut';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import moment, { LocaleSpecifier, Moment } from 'moment';
import {
  CalendarEvent,
  FiltersTimeline,
  Resources,
} from '../../types/timeline';
import {
  GetEffectifTimeLine,
  GetEventsTimeLine,
  GetResourcesTimeLine,
} from 'features/conge/services/Timeline';
import LeaveDetails from '../LeaveDetails';
import { EventInput } from '@fullcalendar/core';
import tippy from 'tippy.js';
import Tippy from '@tippyjs/react';
import useFetchData from 'hooks/useFetch';

import { useTranslation } from 'react-i18next';
import { darkenHexColor } from 'utils/colorUtils';
interface TimelineProps {
  selectedDate: Moment;
  filters: FiltersTimeline;
}

const TimelineSkeleton: React.FC = () => {
  // Generate random skeleton events for realistic loading state
  const generateRandomEvents = () => {
    const events = [];
    for (let row = 0; row < 8; row++) {
      const rowEvents = [];
      let skipNext = 0;

      for (let col = 0; col < 31; col++) {
        if (skipNext > 0) {
          skipNext--;
          continue;
        }

        // 30% chance to have an event
        if (Math.random() > 0.7) {
          // Random span between 1-5 days
          const span = Math.floor(Math.random() * 4) + 1;
          rowEvents.push({ start: col, span });
          skipNext = span - 1;
        }
      }
      events.push(rowEvents);
    }
    return events;
  };

  const skeletonEvents = generateRandomEvents();

  return (
    <div className="minthr-mt-6">
      <style>{`
        .skeleton-loader {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s ease-in-out infinite;
          border-radius: calc(var(--unit-minthr) * 4);
        }

        .skeleton-circle {
          border-radius: 50%;
        }

        .skeleton-timeline-container {
          border: calc(var(--unit-minthr) * 1) solid #e5e7eb;
          overflow: hidden;
          background: white;
        }

        .skeleton-header {
          display: flex;
          background-color: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
        }

        .skeleton-resource-header {
          width: calc(var(--unit-minthr) * 230);
          display: flex;
          align-items: center;
          padding-right: 16px;
          border-right: 1px solid #e5e7eb;
        }

        .skeleton-days-header {
          flex: 1;
          display: grid;
          grid-template-columns: repeat(31, 1fr);
          gap: 0;
        }

        .skeleton-day-cell {
          padding: calc(var(--unit-minthr) * 8) calc(var(--unit-minthr) * 4);
          text-align: center;
          border-right: 1px solid #f3f4f6;
        }

        .skeleton-day-cell:last-child {
          border-right: none;
        }

        .skeleton-row {
          display: flex;
          border-bottom: calc(var(--unit-minthr) * 1) solid #e5e7eb;
          border-radius:0 !important;
          box-shadow: none !important;
        }

        .skeleton-row:last-child {
          border-bottom: none;
        }

        .skeleton-resource-cell {
          width: calc(var(--unit-minthr) * 220);
          display: flex;
          align-items: center;
          border-right: calc(var(--unit-minthr) * 1) solid #e5e7eb;
        }

        .skeleton-events-grid {
          flex: 1;
          display: grid;
          grid-template-columns: repeat(31, 1fr);
          gap: 0;
          position: relative;
        }

        .skeleton-event-cell {
          padding: calc(var(--unit-minthr) * 8) calc(var(--unit-minthr) * 2);
          border-right: calc(var(--unit-minthr) * 1) solid #f3f4f6;
        }

        .skeleton-event-cell:last-child {
          border-right: none;
        }

        .skeleton-event-block {
          position: absolute;
          height: calc(var(--unit-minthr) * 32);
          top: 50%;
          transform: translateY(-50%);
        }

        @keyframes skeleton-loading {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }
      `}</style>

      <div className="skeleton-timeline-container">
        {/* Header */}
        <div className="skeleton-header">
          <div className="skeleton-resource-header">
            <div
              className="skeleton-loader skeleton-circle"
              style={{
                width: 'calc(var(--unit-minthr) * 31)',
                height: 'calc(var(--unit-minthr) * 31)',
                marginRight: 'calc(var(--unit-minthr) * 18)',
                marginLeft: 'calc(var(--unit-minthr) * 8)',
              }}
            ></div>
            <div
              className="skeleton-loader"
              style={{
                width: 'calc(var(--unit-minthr) * 80)',
                height: 'calc(var(--unit-minthr) * 16)',
                marginRight: 'calc(var(--unit-minthr) * 18)',
              }}
            ></div>
            <div
              className="skeleton-loader"
              style={{
                width: 'calc(var(--unit-minthr) * 30)',
                height: 'calc(var(--unit-minthr) * 16)',
                marginLeft: 'calc(var(--unit-minthr) * 28)',
              }}
            ></div>
          </div>
          <div className="skeleton-days-header">
            {[...Array(31)].map((_, i) => (
              <div key={i} className="skeleton-day-cell">
                <div
                  className="skeleton-loader"
                  style={{
                    width: 'calc(var(--unit-minthr) * 24)',
                    height: 'calc(var(--unit-minthr) * 14)',
                    margin: '0 auto calc(var(--unit-minthr) * 4)',
                  }}
                ></div>
                <div
                  className="skeleton-loader"
                  style={{
                    width: 'calc(var(--unit-minthr) * 20)',
                    height: 'calc(var(--unit-minthr) * 10)',
                    margin: '0 auto',
                  }}
                ></div>
              </div>
            ))}
          </div>
        </div>

        {/* Rows */}
        {[...Array(8)].map((_, rowIndex) => (
          <div key={rowIndex} className="skeleton-row">
            {/* Resource Column */}
            <div className="skeleton-resource-cell">
              <div
                className="skeleton-loader skeleton-circle"
                style={{
                  width: 'calc(var(--unit-minthr) * 41)',
                  height: 'calc(var(--unit-minthr) * 41)',
                  marginRight: 'calc(var(--unit-minthr) * 12)',
                  flexShrink: 0,
                }}
              ></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  className="skeleton-loader"
                  style={{
                    width: '90%',
                    height: 'calc(var(--unit-minthr) * 16)',
                    marginBottom: 'calc(var(--unit-minthr) * 6)',
                  }}
                ></div>
                <div
                  className="skeleton-loader"
                  style={{
                    width: '60%',
                    height: 'calc(var(--unit-minthr) * 12)',
                  }}
                ></div>
              </div>
            </div>

            {/* Events Grid */}
            <div className="skeleton-events-grid">
              {[...Array(31)].map((_, cellIndex) => (
                <div key={cellIndex} className="skeleton-event-cell"></div>
              ))}

              {/* Event Blocks */}
              {skeletonEvents[rowIndex].map((event, eventIndex) => {
                const colors = [
                  '#10b981', // green
                  '#f59e0b', // orange
                  '#8b5cf6', // purple
                  '#3b82f6', // blue
                  '#ef4444', // red
                ];
                const color = colors[Math.floor(Math.random() * colors.length)];
                const width = `calc(${(event.span * 100) / 31}% - calc(var(--unit-minthr) * 4))`;
                const left = `calc(${(event.start * 100) / 31}% + calc(var(--unit-minthr) * 2))`;

                return (
                  <div
                    key={eventIndex}
                    className="skeleton-loader skeleton-event-block"
                    style={{
                      width,
                      left,
                      backgroundColor: color,
                      opacity: 0.3,
                    }}
                  ></div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface LeaveSegment {
  demande: string;
  figure: string;
  label: string;
  color: string;
  textColor: string;
  description: string;
}

type TimelineEvent = EventInput & {
  demande?: string;
  fauTitle?: string;
  description?: string;
  isHoliday?: boolean;
  isMerged?: boolean;
  segments?: LeaveSegment[];
};

const isSingleDayEvent = (start: string, end: string): boolean =>
  moment(start).isSame(moment(end), 'day');

const dayKeyOf = (resourceId: string, start: string): string =>
  `${resourceId}__${moment(start).format('YYYY-MM-DD')}`;

/**
 * Builds the FullCalendar events from the API payload.
 *
 * The API can return several leaves for the same collaborator on the same day
 * (e.g. a morning "Déplacement" + an "Absence justifiée" of a few hours). In the
 * month timeline every event is drawn as a full-day bar, so those leaves land on
 * the exact same cell and overlap — only the top one stays visible.
 *
 * To show them all, same-day leaves of a resource are merged into a single event
 * whose content is painted as side-by-side colored segments (one per leave, each
 * clickable with its own tooltip). Lone leaves and multi-day leaves are emitted
 * unchanged.
 */
const buildTimelineEvents = (events: CalendarEvent[]): TimelineEvent[] => {
  const singles: TimelineEvent[] = events.map((e) => ({
    id: e.demande,
    fauTitle: e.fauTitle,
    description: e.description,
    start: e.start,
    end: e.end,
    resourceId: e.resourceId,
    textColor: darkenHexColor(e.color, 35),
    borderColor: darkenHexColor(e.color, 35),
    backgroundColor: e.color,
    demande: e.demande,
    isHoliday: e.isHoliday,
  }));

  // Group non-holiday, single-day leaves by resource + calendar day.
  const groups = new Map<string, TimelineEvent[]>();
  singles.forEach((e) => {
    if (e.isHoliday || !isSingleDayEvent(e.start as string, e.end as string)) {
      return;
    }
    const key = dayKeyOf(e.resourceId as string, e.start as string);
    const group = groups.get(key);
    if (group) group.push(e);
    else groups.set(key, [e]);
  });

  const mergedKeys = new Set<string>();
  const mergedEvents: TimelineEvent[] = [];
  groups.forEach((group, key) => {
    if (group.length < 2) return; // a lone leave keeps its own bar
    mergedKeys.add(key);
    // Earliest leave first, so a morning one sits on the left.
    const ordered = [...group].sort(
      (a, b) =>
        moment(a.start as string).valueOf() -
          moment(b.start as string).valueOf() ||
        moment(a.end as string).valueOf() - moment(b.end as string).valueOf(),
    );
    const day = moment(group[0].start as string).format('YYYY-MM-DD');
    mergedEvents.push({
      id: `merged-${key}`,
      resourceId: group[0].resourceId,
      start: `${day}T00:00:00`,
      end: `${day}T23:59:59`,
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      isHoliday: false,
      isMerged: true,
      segments: ordered.map((e) => ({
        demande: e.demande ?? '',
        figure: (e.fauTitle ?? '').split(' ')[0],
        label: e.fauTitle ?? '',
        color: e.backgroundColor ?? '',
        textColor: e.textColor ?? '',
        description: e.description ?? '',
      })),
    });
  });

  const passthrough = singles.filter((e) => {
    if (e.isHoliday || !isSingleDayEvent(e.start as string, e.end as string)) {
      return true;
    }
    return !mergedKeys.has(dayKeyOf(e.resourceId as string, e.start as string));
  });

  return [...passthrough, ...mergedEvents];
};

const CampaignResource: React.FC<{
  title: string;
  id: string;
  extendedProps: EventInput;
}> = ({ title, id, extendedProps }) => (
  <Link href={`/employee/${id}/conge/calendar`}>
    <div className="minthr-flex minthr-flex-items-center">
      <UserShortCut
        className="minthr-avatar minthr-avatar-m minthr-mr-2"
        src={`upload/profileimages/${id && id}/${extendedProps.imageUrl && extendedProps.imageUrl}`}
        alt=""
      />
      <div>
        <h5>{title}</h5>
        <p className="text-s minthr-mb-0">{extendedProps.fonction}</p>
      </div>
    </div>
  </Link>
);

const Timeline: React.FC<TimelineProps> = ({ selectedDate, filters }) => {
  const { openSidebar } = useSidebarManager();
  const { t } = useTranslation();
  const calendarRef = useRef<FullCalendar | null>(null);
  const resources = useRef<Resources[]>();
  const pageRef = useRef(1);
  const isFetchMore = useRef(false);
  const firstLoad = useRef(false);

  const {
    data: resourcesData,
    loading: isLoadingResources,
    refetch: refetchResources,
  } = useFetchData(GetResourcesTimeLine, [filters]);

  const {
    data: events,
    loading: isLoadingEvents,
    refetch: refetchEvents,
  } = useFetchData(GetEventsTimeLine, [filters]);

  const {
    data: effectif,
    loading: isLoadingEffectif,
    refetch: refetchEffectif,
  } = useFetchData(GetEffectifTimeLine, [filters]);

  const isLoading = isLoadingResources || isLoadingEvents || isLoadingEffectif;
  const showLoader = isLoading && !firstLoad.current;
  resources.current = resourcesData ?? [];

  useEffect(() => {
    if (!isLoading && resourcesData && events && effectif) {
      firstLoad.current = true;
    }
  }, [isLoading, resourcesData, events, effectif]);

  const refetchAll = (): void => {
    refetchResources();
    refetchEvents();
    refetchEffectif();
  };

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();

    if (!calendarApi) return;

    const scrollerEl = (
      calendarApi as unknown as { el: HTMLDivElement }
    ).el.querySelectorAll('.fc-scroller-harness .fc-scroller')[3];

    const handleScroll = async (): Promise<void> => {
      if (
        scrollerEl.scrollTop + scrollerEl.clientHeight >=
          scrollerEl.scrollHeight - 50 &&
        !isFetchMore.current
      ) {
        try {
          isFetchMore.current = true;
          pageRef.current += 1;

          const res = await GetResourcesTimeLine({
            ...filters,
            page: pageRef.current,
          });
          if (res && resources.current) {
            res?.forEach((res) => {
              calendarApi.addResource(
                {
                  id: res.user?.id,
                  title: res.user?.fullName,
                  fonction: res.user?.fonctionName,
                  imageUrl: res.user?.image,
                },
                false,
              );
            });
          }
        } finally {
          isFetchMore.current = false;
        }
      }
    };

    scrollerEl?.addEventListener('scroll', handleScroll);

    return (): void => {
      scrollerEl?.removeEventListener('scroll', handleScroll);
    };
  }, [calendarRef, isLoading]);

  if (showLoader || isLoading) {
    return <TimelineSkeleton />;
  }

  if (!events || !resources || !effectif) {
    return;
  }

  const renderEventContent = (eventInfo: EventInput): void => {
    const { isHoliday, description, isMerged } = eventInfo.event.extendedProps;

    // Merged events paint their own segments, each with its own (native) tooltip.
    if (isMerged) return;

    if (isHoliday) {
      const eventMainEl = eventInfo.el.querySelector('.fc-event-main');
      eventMainEl.innerHTML = '';
      eventMainEl.innerHTML = '<i class="fa fa-bookmark"></i>';
      eventInfo.el.classList.add('minthr-text-align-center', 'minthr-w-100');
      eventInfo.el.style.backgroundColor = 'transparent';
      eventInfo.el.style.borderColor = 'transparent';
      eventInfo.el.style.color = '#69696991';
      eventInfo.el.style.fontSize = 'calc(var(--mintrh-unit) * 25)';
      tippy(eventInfo.el, {
        content: description,
      });
    } else {
      tippy(eventInfo.el, {
        content: description,
      });
    }
  };

  return (
    <FullCalendar
      key="calendar-instance"
      ref={calendarRef}
      initialDate={selectedDate.format('YYYY-MM-DD')}
      headerToolbar={{
        left: '',
        center: '',
        right: '',
      }}
      editable={false}
      droppable={false}
      initialView="resourceTimelineMonth"
      refetchResourcesOnNavigate={true}
      slotLabelContent={(e) => {
        const culture = readStoredLanguage() as LocaleSpecifier | null;
        if (e.view.type == 'resourceTimelineMonth') {
          let day;

          if (culture === 'ar') {
            day = moment(e.date).format('dddd').substring(2, 5);
          } else {
            day = moment(e.date).format('dddd').substring(0, 3);
          }

          const num = moment(e.date).format('DD');
          return {
            html: `<div class="minthrCalendar-date">${num} <span>${day}</span></div>`,
          };
        }
      }}
      aspectRatio={1.5}
      resourceAreaWidth="calc(var(--unit-minthr)* 217)"
      resourceOrder="total"
      resourceAreaHeaderContent={
        <div className="minthr-flex minthr-flex-items-center">
          <div className="minthr-icon-bg minthr-icon-bg-s minthr-icon-bg-secondary minthr-icon-circle minthr-mr-3">
            <img className="minthr-icon-m " src="/icons/user.svg" alt="" />
          </div>
          <h4>{t('Reporting.headcount')}</h4>
          <span className="minthr-ml-auto minthr-color-primary effectif">
            {effectif.filterUsers + '/' + effectif.totalUsers}
          </span>
        </div>
      }
      displayEventTime={false}
      eventContent={(arg) => {
        // Several same-day leaves are painted as side-by-side colored segments.
        if (arg.event.extendedProps?.isMerged) {
          const segments = (arg.event.extendedProps.segments ??
            []) as LeaveSegment[];
          return (
            <div className="minthr-leave-split">
              {segments.map((seg) => (
                <Tippy
                  key={seg.demande}
                  content={[seg.description, seg.label]
                    .filter(Boolean)
                    .join(' · ')}
                >
                  <div
                    onClick={(ev) => {
                      ev.stopPropagation();
                      openSidebar(LeaveDetails, true, {
                        id: seg.demande,
                        deleteEvent: () => arg.event.remove(),
                        refetch: refetchAll,
                      });
                    }}
                    className="minthr-leave-split-seg"
                    style={{
                      backgroundColor: seg.color,
                      color: seg.textColor,
                    }}
                  >
                    {seg.figure}
                  </div>
                </Tippy>
              ))}
            </div>
          );
        }
        const duration = arg.event.extendedProps?.fauTitle?.split(' ') ?? null;
        if (!duration) return null;
        return (
          <div
            className="minthr-text-align-center "
            style={{ color: arg.textColor }}
          >
            {duration[0]}{' '}
            <span style={{ color: 'inherit' }}>{duration[1]}</span>
          </div>
        );
      }}
      eventClassNames={(arg) => {
        if (arg.event.extendedProps.isMerged) return ['minthr-merged-leave'];
        const eventClass = arg.event._def.extendedProps.class;
        return [eventClass];
      }}
      eventClick={(arg) => {
        // Merged events handle clicks per segment inside eventContent.
        if (arg.event.extendedProps.isMerged) return;

        const calendarApi = calendarRef.current?.getApi();

        const deleteEvent = (): void => {
          const event = calendarApi?.getEventById(
            arg.event.extendedProps.demande,
          );
          if (event) {
            event.remove();
          }
        };

        if (!arg.event.extendedProps.isHoliday) {
          openSidebar(LeaveDetails, true, {
            id: arg.event.extendedProps.demande,
            deleteEvent,
            refetch: refetchAll,
          });
        } else {
          const clickedDate = arg.event.start;
          const resourceId = arg.event.getResources()[0]?.id;

          if (clickedDate && resourceId && calendarApi) {
            const allEvents = calendarApi.getEvents();
            const overlappingLeave = allEvents.find((event) => {
              const eventResourceId = event.getResources()[0]?.id;
              const isLeave = !event.extendedProps.isHoliday;
              const eventStart = event.start;
              const eventEnd = event.end || event.start;

              if (!isLeave || eventResourceId !== resourceId || !eventStart) {
                return false;
              }

              const holidayTime = clickedDate.getTime();
              const startTime = eventStart.getTime();
              const endTime = eventEnd ? eventEnd.getTime() : startTime;

              return holidayTime >= startTime && holidayTime < endTime;
            });

            if (overlappingLeave) {
              const deleteOverlappingEvent = (): void => {
                const event = calendarApi.getEventById(
                  overlappingLeave.extendedProps.demande,
                );
                if (event) {
                  event.remove();
                }
              };

              openSidebar(LeaveDetails, true, {
                id: overlappingLeave.extendedProps.demande,
                deleteEvent: deleteOverlappingEvent,
                refetch: refetchAll,
              });
            }
          }
        }
      }}
      resources={resources.current?.map(({ user }: Resources) => ({
        id: user?.id,
        title: user?.fullName,
        fonction: user?.fonctionName,
        imageUrl: user?.image,
      }))}
      resourceLabelContent={(arg) => {
        return (
          <CampaignResource
            title={arg.resource.title}
            id={arg.resource.id}
            extendedProps={arg.resource.extendedProps}
          />
        );
      }}
      moreLinkContent={(arg) => {
        return arg.shortText;
      }}
      eventDidMount={renderEventContent}
      businessHours={{
        daysOfWeek: [1, 2, 3, 4, 5],
      }}
      viewClassNames="minthr-calendar"
      plugins={[resourceTimelinePlugin, interactionPlugin, dayGridPlugin]}
      eventSources={[
        {
          events: buildTimelineEvents(events),
        },
      ]}
      schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
    />
  );
};

export default Timeline;
